import React, { useEffect, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { formatRemaining } from '../services/auth/rateLimiter';

interface Props {
  /**
   * Total remaining lockout at the moment the banner was shown, in ms.
   * The banner runs its own 1s tick-down internally so the parent
   * doesn't have to manage setInterval.
   */
  remainingMs: number;
  /** The failure count (for the "After N attempts" phrasing). */
  count: number;
  /** Fires once when the countdown reaches 0. */
  onExpired?: () => void;
}

/**
 * Yellow caution banner. Counts down to 0 and then fires `onExpired`
 * so the parent can dismiss it + unlock the submit button.
 */
export const RateLimitBanner: React.FC<Props> = ({
  remainingMs,
  count,
  onExpired,
}) => {
  const [ms, setMs] = useState(remainingMs);

  useEffect(() => {
    setMs(remainingMs);
  }, [remainingMs]);

  useEffect(() => {
    if (ms <= 0) {
      if (onExpired) onExpired();
      return;
    }
    const interval = setInterval(() => {
      setMs((prev) => {
        const next = prev - 1000;
        if (next <= 0) {
          clearInterval(interval);
          if (onExpired) onExpired();
          return 0;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
    // Intentionally not depending on `onExpired` so that a re-rendered
    // parent doesn't reset the interval.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ms <= 0]);

  if (ms <= 0) return null;

  return (
    <View style={styles.banner} accessibilityRole="alert">
      <Text style={styles.title}>Too many attempts</Text>
      <Text style={styles.body}>
        {count} failed attempts. Try again in {formatRemaining(ms)}.
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  banner: {
    backgroundColor: '#FFF4E0',
    borderWidth: 1,
    borderColor: '#E6A23C',
    borderRadius: 8,
    padding: 12,
    marginVertical: 8,
  },
  title: {
    color: '#92400E',
    fontWeight: '600',
    fontSize: 14,
    marginBottom: 4,
  },
  body: {
    color: '#5A3F0F',
    fontSize: 13,
  },
});

export default RateLimitBanner;
